// Shared layout for the legal pages (/legal/:slug): page nav, title, the "draft" notice and the
// visible placeholder style. The texts are careful drafts, not legal advice (SPEC §8).
import type { ReactNode } from "react";
import { Link, useTitle } from "../../lib/router";

export type LegalSlug = "imprint" | "privacy" | "terms" | "licence" | "cookies";

export const LEGAL_NAV: { slug: LegalSlug; title: string }[] = [
  { slug: "imprint", title: "Imprint" },
  { slug: "privacy", title: "Privacy Policy" },
  { slug: "terms", title: "Terms of Use" },
  { slug: "licence", title: "Licence" },
  { slug: "cookies", title: "Cookie Notice" },
];

/** A value the operator still has to fill in, shown highlighted as "[NAME]". */
export function Ph({ children }: { children: string }) {
  return <mark className="placeholder">[{children}]</mark>;
}

/** Link to another legal page. */
export function LegalLink({ to, children }: { to: LegalSlug; children: ReactNode }) {
  return <Link href={`/legal/${to}`}>{children}</Link>;
}

/** When the current legal texts took effect (update with every change to them). */
export const LEGAL_EFFECTIVE_DATE = "3 November 2026";

/** How to reach us (no email address is published while the operator is in founding stage). */
export const CONTACT = "contact us via the official accounts (tba)";

/** The operator (Imprint, Privacy Policy, Terms): in founding stage, no personal details published. */
export function OperatorAddress() {
  return (
    <address>
      <strong>THE INFINITE BEAT PROJECT G47 BERLIN IS IN FOUNDING STAGE</strong>
      <br />
      Berlin, Germany
      <br />
      Contact via the official accounts (tba)
    </address>
  );
}

export function LegalLayout({
  slug,
  title,
  subtitle,
  children,
}: {
  slug: LegalSlug;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  useTitle(title);
  return (
    <main id="main" className="page narrow legal">
      <nav className="legal-nav" aria-label="Legal pages">
        <ul>
          {LEGAL_NAV.map((p) => (
            <li key={p.slug}>
              <Link href={`/legal/${p.slug}`} aria-current={p.slug === slug ? "page" : undefined}>
                {p.title}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      <h1>{title}</h1>
      {subtitle && <p className="legal-subtitle muted">{subtitle}</p>}
      <div className="legal-draft" role="note">
        <strong>Draft — to be reviewed.</strong> This text is a carefully prepared draft, not legal advice; it will be
        reviewed before The Infinite opens to the public.
      </div>
      <p className="legal-effective muted">Effective date: {LEGAL_EFFECTIVE_DATE}</p>
      {children}
    </main>
  );
}
